import type { TokenRepository } from "@/domains/tokens"
import type { TokenStore } from "@/domains/tokens/token-store"
import { LcAddress } from "@/domains/core"

import type { ManualClPoolApiData, ManualClPoolStoreDAO } from "./types"

/**
 * Convert raw GraphQL response to Manual CL Pool Store DAO
 * Handles address normalization, token lookup, and data transformation
 */
export function convertApiResponseToManualClPoolStoreDAO(
  apiItem: ManualClPoolApiData,
  tokenRepository: TokenRepository,
): ManualClPoolStoreDAO | null {
  // console.log("[convertApiResponseToManualClPoolStoreDAO] Processing pool:", apiItem.address)

  // Look up tokens from repository
  const token0Address = new LcAddress(apiItem.token0.address)
  const token1Address = new LcAddress(apiItem.token1.address)

  // console.log("[convertApiResponseToManualClPoolStoreDAO] Looking up tokens:", {
  //   token0Address: token0Address.toString(),
  //   token1Address: token1Address.toString(),
  // })

  const token0 = tokenRepository.find(token0Address)
  const token1 = tokenRepository.find(token1Address)

  // If tokens are not found, we can't create the pool store
  if (!token0 || !token1) {
    console.warn("[convertApiResponseToManualClPoolStoreDAO] Tokens not found in repository:", {
      poolAddress: apiItem.address,
      token0Found: !!token0,
      token1Found: !!token1,
      token0Address: token0Address.toString(),
      token1Address: token1Address.toString(),
    })
    return null
  }

  // console.log("[convertApiResponseToManualClPoolStoreDAO] Tokens found:", {
  //   token0Symbol: token0.symbol,
  //   token1Symbol: token1.symbol,
  // })

  // Convert fee tier to fee percentage
  // Fee tier is in basis points (e.g., 100 = 0.01%, 3000 = 0.3%, 10000 = 1%)
  const fee = apiItem.feeTier / 10000

  // Parse reserves from the exact decimal strings returned by the GraphQL
  // validator. The numeric TVL fields remain available for display-only UI.
  const reserve0 = parseTokenAmountToUnits(token0, apiItem.totalValueLockedToken0Raw) ?? 0n
  const reserve1 = parseTokenAmountToUnits(token1, apiItem.totalValueLockedToken1Raw) ?? 0n

  const dao: ManualClPoolStoreDAO = {
    address: new LcAddress(apiItem.address),
    decimals: 18, // Standard for CL pools
    fee,
    feeTier: apiItem.feeTier,
    tickSpacing: apiItem.tickSpacing ?? 64,
    isStable: false, // CL pools are always volatile
    reserve0,
    reserve1,
    tags: ["manual-cl"],
    tvlUsd: apiItem.totalValueLockedUsd,
    totalValueLockedToken0: apiItem.totalValueLockedToken0,
    totalValueLockedToken1: apiItem.totalValueLockedToken1,
    token0,
    token1,
    volumeUsd: apiItem.volumeUsd,
    feesUsd: apiItem.feesUsd,
    liquidityProviderCount: apiItem.liquidityProviderCount,
    liquidity: parseLiquidityBigInt(apiItem.liquidity),
    currentTick: apiItem.currentTick,
    currentSqrtPriceX96: apiItem.currentSqrtPriceX96,
    poolDayData: apiItem.poolDayData ?? [],
    poolHourData: apiItem.poolHourData ?? [],
  }

  // console.log("[convertApiResponseToManualClPoolStoreDAO] Created DAO:", {
  //   address: dao.address.toString(),
  //   displayName: `${token0.symbol}/${token1.symbol}`, // Computed, not stored
  //   feeTier: dao.feeTier,
  //   fee: dao.fee,
  //   tvlUsd: dao.tvlUsd,
  //   volumeUsd: dao.volumeUsd,
  //   feesUsd: dao.feesUsd,
  //   token0Symbol: dao.token0.symbol,
  //   token1Symbol: dao.token1.symbol,
  //   reserve0: reserve0.toString(),
  //   reserve1: reserve1.toString(),
  // })

  return dao
}

/**
 * Safely parse an indexer-supplied `Pool.liquidity` (which arrives as a
 * decimal string) into a `bigint`. Returns `0n` on missing, empty or
 * malformed values — downstream APR math treats `0n` as "unknown" and
 * falls through to its own safety guards rather than blowing up.
 */
function parseLiquidityBigInt(value: string | undefined): bigint {
  if (!value) return 0n
  try {
    return BigInt(value)
  } catch {
    return 0n
  }
}

function parseTokenAmountToUnits(token: TokenStore, value: string): bigint | undefined {
  try {
    return token.parseUnits(clampDecimalScale(value, token.decimals))
  } catch {
    return undefined
  }
}

function clampDecimalScale(value: string, decimals: number): string {
  const [integerPart, fractionalPart] = value.split(".")
  if (fractionalPart === undefined || decimals < 0) return value
  if (decimals === 0) return integerPart ?? "0"
  return `${integerPart ?? "0"}.${fractionalPart.slice(0, decimals)}`
}
